import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { SINGLE_DAY_SAVE_FORMAT } from '../../../constants/date';
import {
  formatDate,
  isDateInPast,
  isDateSoonToExpire,
} from '../../../utility/dateUtils';
import {
  isEmpty,
  validateFileType,
  validateLength,
} from '../../../utility/validation';

const DOCUMENT_FORM_FIELD_NAMES = {
  DOCUMENT_TYPE_NAME: 'documentTypeName',
  DESCRIPTION: 'description',
  HAS_SIGNING_DATE: 'has_signing_date',
  SIGNING_DATE: 'signing_date',
  HAS_EXPIRE_DATE: 'has_expire_date',
  EXPIRE_DATE: 'expire_date',
  UPLOAD_FILE: 'upload_file',
};

const DOCUMENT_UPLOAD_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const prepareDocumentExpiryFlags = (documentCategories = []) => {
  let hasExpired = false;
  let hasSoonToExpire = false;

  documentCategories.forEach(category => {
    (category.types || []).forEach(type => {
      const expireDate = type.document?.expire_date;

      if (!expireDate || (hasExpired && hasSoonToExpire)) {
        return;
      }

      if (!hasExpired && isDateInPast(expireDate)) {
        hasExpired = true;
      }

      if (
        !hasSoonToExpire &&
        isDateSoonToExpire(expireDate, category.expire_warning_days)
      ) {
        hasSoonToExpire = true;
      }
    });
  });

  return { hasExpired, hasSoonToExpire };
};

export const getDocumentFormInitialData = documentType => {
  const { document } = documentType;
  const hasSigningDate = !!document?.signing_date;
  const hasExpireDate = !!document?.expire_date;

  return {
    ...(document?.id ? { id: document?.id } : {}),
    [DOCUMENT_FORM_FIELD_NAMES.DOCUMENT_TYPE_NAME]: documentType?.name || '',
    [DOCUMENT_FORM_FIELD_NAMES.DESCRIPTION]: document?.description || '',
    [DOCUMENT_FORM_FIELD_NAMES.HAS_SIGNING_DATE]: hasSigningDate,
    [DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE]: hasSigningDate
      ? document.signing_date
      : null,
    [DOCUMENT_FORM_FIELD_NAMES.HAS_EXPIRE_DATE]: hasExpireDate,
    [DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE]: hasExpireDate
      ? document.expire_date
      : null,
    [DOCUMENT_FORM_FIELD_NAMES.UPLOAD_FILE]: document?.file || null,
  };
};

export const documentFormFields = classes => {
  return [
    {
      name: DOCUMENT_FORM_FIELD_NAMES.DOCUMENT_TYPE_NAME,
      type: FIELD_TYPES.TEXT,
      translationKey: 'documentType',
      isDisabled: true,
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.DESCRIPTION,
      type: FIELD_TYPES.RICH_TEXT_EDITOR,
      translationKey: 'description',
      validators: [
        {
          validator: fieldValue => validateLength(fieldValue, 0, 500),
          type: 'descriptionMaxLength',
        },
      ],
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.HAS_SIGNING_DATE,
      type: FIELD_TYPES.SWITCH,
      labelClass: classes.switchLabel,
      translationKey: 'signingDate',
      shouldRender: () => true,
      dependants: [
        {
          name: DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE,
          resetFnc: async (prevValue, currentValue) => {
            if (currentValue) {
              return (
                currentValue[DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE] ||
                formatDate(new Date(), SINGLE_DAY_SAVE_FORMAT)
              );
            }

            return null;
          },
        },
      ],
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE,
      type: FIELD_TYPES.DATE_PICKER,
      translationKey: 'signingDateValue',
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      isToggleable: true,
      shouldRender: values =>
        values[DOCUMENT_FORM_FIELD_NAMES.HAS_SIGNING_DATE],
      getMaxDate: (value, values) =>
        values?.[DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE] || undefined,
      validators: [
        {
          validator: (value, values) => {
            if (!values[DOCUMENT_FORM_FIELD_NAMES.HAS_EXPIRE_DATE]) {
              return true;
            }

            return value <= values[DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE];
          },
          shouldPassFormValues: true,
          type: 'invalidSigningDate',
        },
      ],
      dependants: [
        {
          name: DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE,
          resetFnc: async (prevValue, currentValue, expireDateValue) =>
            expireDateValue,
        },
      ],
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.HAS_EXPIRE_DATE,
      type: FIELD_TYPES.SWITCH,
      translationKey: 'expireDate',
      labelClass: classes.switchLabel,
      shouldRender: () => true,
      dependants: [
        {
          name: DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE,
          resetFnc: async (prevValue, currentValue) => {
            if (currentValue) {
              return (
                currentValue[DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE] ||
                formatDate(new Date(), SINGLE_DAY_SAVE_FORMAT)
              );
            }

            return null;
          },
        },
      ],
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.EXPIRE_DATE,
      type: FIELD_TYPES.DATE_PICKER,
      translationKey: 'expireDateValue',
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      isToggleable: true,
      shouldRender: values => values[DOCUMENT_FORM_FIELD_NAMES.HAS_EXPIRE_DATE],
      getMinDate: (value, values) =>
        values?.[DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE] || undefined,
      validators: [
        {
          validator: (value, values) => {
            if (!values[DOCUMENT_FORM_FIELD_NAMES.HAS_SIGNING_DATE]) {
              return true;
            }

            return value >= values[DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE];
          },
          shouldPassFormValues: true,
          type: 'invalidExpiryDate',
        },
      ],
      dependants: [
        {
          name: DOCUMENT_FORM_FIELD_NAMES.SIGNING_DATE,
          resetFnc: async (prevValue, currentValue, signingDateValue) =>
            signingDateValue,
        },
      ],
    },
    {
      name: DOCUMENT_FORM_FIELD_NAMES.UPLOAD_FILE,
      type: FIELD_TYPES.UPLOAD_FILE,
      translationKey: 'uploadFile',
      isMultiple: false,
      types: {
        'application/pdf': ['.pdf'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx'],
      },
      required: true,
      validators: [
        {
          type: 'fileType',
          validator: value =>
            validateFileType(value, DOCUMENT_UPLOAD_MIME_TYPES),
        },
        {
          validator: value => {
            return validateLength(value?.name, 0, 100);
          },
          type: 'fileLength',
        },
        {
          validator: isEmpty,
          type: 'required',
        },
      ],
    },
  ];
};
